import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Lightbox from '../components/Lightbox';

const jacuzziCtx = require.context('../images/jacuzzi', false, /\.(png|jpe?g|svg)$/);
const allImages = jacuzziCtx.keys().map(k => jacuzziCtx(k));

const reviewsIT = [
    {
        stars: '★★★★★',
        text: "Struttura meravigliosa, camere pulitissime e colazione da sogno. I gestori sono di una gentilezza rara, ci hanno dato ottimi consigli su cosa visitare ad Anzio. Torneremo sicuramente!",
        name: "Marco R.", source: "Booking.com", initial: "M",
    },
    {
        stars: '★★★★★',
        text: "La Suite Jacuzzi è un'esperienza indimenticabile. La vasca con cromoterapia è semplicemente stupenda, l'ambiente curatissimo. Perfetta per il nostro anniversario.",
        name: "Valentina M.", source: "Google Reviews", initial: "V",
    },
    {
        stars: '★★★★★',
        text: "Posizione ottima tra Anzio e Nettuno, a pochi minuti dal mare. Camera spaziosa e silenziosa, colazione ottima. Il parcheggio riservato è comodissimo.",
        name: "Francesco D.", source: "Booking.com", initial: "F",
    },
];

const features = {
    it: [
        { icon: '🛁', title: 'Vasca Idromassaggio', desc: 'Ampia vasca integrata con idromassaggio e luci LED cromoterapia.' },
        { icon: '🌈', title: 'LED Cromoterapia', desc: 'Illuminazione multicolore per creare l\'atmosfera perfetta.' },
        { icon: '📺', title: 'Smart TV & Netflix', desc: 'Smart TV 55" con Netflix, Prime Video e streaming.' },
        { icon: '🛋️', title: 'Living Privato', desc: 'Area living dedicata con divano per rilassarsi.' },
        { icon: '❄️', title: 'Aria Condizionata', desc: 'Clima perfetto in ogni stagione.' },
        { icon: '🌿', title: 'Vista Giardino', desc: 'Affaccio sul giardino privato della struttura.' },
        { icon: '🍳', title: 'Colazione Inclusa', desc: 'Colazione con prodotti freschi ogni mattina.' },
        { icon: '🚗', title: 'Parcheggio Riservato', desc: 'Posto auto con ricarica elettrica incluso.' },
    ],
    en: [
        { icon: '🛁', title: 'Jacuzzi', desc: 'Large built-in jacuzzi with chromotherapy LED lighting.' },
        { icon: '🌈', title: 'LED Chromotherapy', desc: 'Multicolour LED lighting to set the perfect atmosphere.' },
        { icon: '📺', title: 'Smart TV & Netflix', desc: '55" Smart TV with Netflix, Prime Video and streaming.' },
        { icon: '🛋️', title: 'Private Living Area', desc: 'Dedicated lounge area with sofa for relaxing.' },
        { icon: '❄️', title: 'Air Conditioning', desc: 'Perfect temperature all year round.' },
        { icon: '🌿', title: 'Garden View', desc: 'Overlooking the private garden of the property.' },
        { icon: '🍳', title: 'Breakfast Included', desc: 'Fresh breakfast products every morning.' },
        { icon: '🚗', title: 'Reserved Parking', desc: 'Parking spot with electric charging included.' },
    ],
};

const faqData = {
    it: [
        { q: "Cos'è incluso nella Suite Jacuzzi?", a: "La Suite Jacuzzi include colazione ogni mattina, Wi-Fi gratuito, parcheggio riservato con ricarica elettrica, Smart TV con Netflix, aria condizionata e uso illimitato della vasca idromassaggio con cromoterapia." },
        { q: "Come si prenota la Suite Jacuzzi?", a: "Puoi prenotare direttamente su WhatsApp al +39 392 872 9907 o via email a almasuitebb@gmail.com. Ti risponderemo entro poche ore con la conferma." },
        { q: "Qual è il check-in e il check-out?", a: "Check-in dalle ore 14:00, check-out entro le ore 11:00. Orari flessibili su richiesta, soggetti a disponibilità." },
        { q: "La suite è adatta per coppie?", a: "Assolutamente. La Suite Jacuzzi è pensata per coppie che cercano un'esperienza romantica. Perfetta per anniversari, compleanni o un weekend di totale relax." },
        { q: "Quanto dista il mare dalla struttura?", a: "La struttura si trova a pochi chilometri dalle spiagge di Anzio e Nettuno. In auto ci si arriva in meno di 10 minuti." },
    ],
    en: [
        { q: "What is included in the Jacuzzi Suite?", a: "The Jacuzzi Suite includes fresh breakfast every morning, free Wi-Fi, reserved parking with electric charging, Smart TV with Netflix, air conditioning, and unlimited use of the chromotherapy jacuzzi." },
        { q: "How do I book the Jacuzzi Suite?", a: "Book directly via WhatsApp at +39 392 872 9907 or by email at almasuitebb@gmail.com. We reply within a few hours to confirm." },
        { q: "What are the check-in and check-out times?", a: "Check-in from 14:00, check-out by 11:00. Flexible times available on request, subject to availability." },
        { q: "Is the suite suitable for couples?", a: "Absolutely. The Jacuzzi Suite is designed for couples seeking a romantic experience. Perfect for anniversaries, birthdays or a full relaxation weekend." },
        { q: "How far is the sea from the property?", a: "The property is a few kilometres from the beaches of Anzio and Nettuno. By car it takes less than 10 minutes." },
    ],
};

function SuiteJacuzziPage() {
    const { isEnLang } = useLanguage();
    const [openFaq, setOpenFaq] = useState(null);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const { ref: introRef, inView: introInView } = useInView({ triggerOnce: true, threshold: 0.1 });
    const { ref: featRef,  inView: featInView  } = useInView({ triggerOnce: true, threshold: 0.1 });
    const { ref: faqRef,   inView: faqInView   } = useInView({ triggerOnce: true, threshold: 0.1 });

    useEffect(() => {
        document.title = isEnLang
            ? 'Jacuzzi Suite – B&B Alma Suite Anzio | Private Jacuzzi with Chromotherapy'
            : 'Suite Jacuzzi – B&B Alma Suite Anzio | Vasca Idromassaggio Privata con Cromoterapia';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', isEnLang
            ? 'The Jacuzzi Suite at B&B Alma Suite: brand new 2026, private jacuzzi with LED chromotherapy, Smart TV, private living area. Perfect for couples. Between Anzio and Nettuno.'
            : 'La Suite Jacuzzi del B&B Alma Suite: nuova costruzione 2026, vasca idromassaggio privata con LED cromoterapia, Smart TV, zona living privata. Perfetta per coppie. Tra Anzio e Nettuno.'
        );
    }, [isEnLang]);

    const faqs  = isEnLang ? faqData.en  : faqData.it;
    const feats = isEnLang ? features.en : features.it;
    const bannerImg = allImages.length > 0 ? allImages[0] : '';

    const T = isEnLang ? {
        suiteLabel:   'Exclusive Suite',
        bannerTitle:  'The Jacuzzi Suite',
        bannerSub:    'Your private retreat between Anzio and Nettuno',
        introLabel:   'Discover',
        introTitle:   'A Unique Experience on the Lazio Coast',
        introDesc:    "The Jacuzzi Suite at Alma Suite is brand new, inaugurated in 2026. A fully equipped room with a large chromotherapy LED jacuzzi, a private living area, Smart TV and all the comforts of a modern eco-friendly property. Located between Anzio and Nettuno, just minutes from the sea — ideal for a romantic escape or a total relaxation weekend.",
        galleryTitle: 'The Suite in Photos',
        gallerySub:   'Every detail has been crafted for your experience',
        featTitle:    'Everything Included',
        featSub:      'Every detail designed for your maximum comfort',
        faqTitle:     'Frequently Asked Questions',
        ctaTitle:     'Book Your Perfect Night',
        ctaSub:       'Limited availability — contact us now to reserve your date',
        ctaBook:      'Book via WhatsApp',
        ctaEmail:     'Send an Email',
    } : {
        suiteLabel:   'Suite Esclusiva',
        bannerTitle:  'La Suite Jacuzzi',
        bannerSub:    'Il tuo rifugio privato tra Anzio e Nettuno',
        introLabel:   'Scopri',
        introTitle:   'Un\'Esperienza Unica sulla Costa Laziale',
        introDesc:    "La Suite Jacuzzi dell'Alma Suite è di nuova costruzione, inaugurata nel 2026. Una camera completamente attrezzata con un'ampia vasca idromassaggio con luci LED cromoterapia, zona living privata, Smart TV e tutto il comfort di un alloggio moderno ed ecosostenibile. Tra Anzio e Nettuno, a pochi chilometri dal mare — ideale per una fuga romantica o un weekend di totale relax.",
        galleryTitle: 'La Suite in Foto',
        gallerySub:   'Ogni dettaglio è stato curato per la tua esperienza',
        featTitle:    'Tutto Incluso',
        featSub:      'Ogni dettaglio pensato per il tuo massimo comfort',
        faqTitle:     'Domande Frequenti',
        ctaTitle:     'Prenota la Tua Notte Perfetta',
        ctaSub:       'Disponibilità limitata — contattaci ora per riservare la tua data',
        ctaBook:      'Prenota su WhatsApp',
        ctaEmail:     'Scrivici via Email',
    };

    return (
        <div className="suite-page">

            {/* Banner */}
            <div className="suite-banner" style={{ backgroundImage: `url(${bannerImg})` }}>
                <div className="suite-banner-overlay">
                    <div className="suite-banner-text">
                        <span className="suite-banner-label">{T.suiteLabel}</span>
                        <h1>{T.bannerTitle}</h1>
                        <p>{T.bannerSub}</p>
                    </div>
                </div>
            </div>

            {/* Intro */}
            <section className="suite-intro">
                <div className="l-768">
                    <motion.div
                        ref={introRef}
                        initial={{ opacity: 0, y: 40 }}
                        animate={introInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="section-label">{T.introLabel}</span>
                        <h2>{T.introTitle}</h2>
                        <p>{T.introDesc}</p>
                    </motion.div>
                </div>
            </section>

            {/* Gallery */}
            <section className="suite-gallery">
                <div className="suite-gallery-title">
                    <h2>{T.galleryTitle}</h2>
                    <p>{T.gallerySub}</p>
                </div>
                <div className="suite-gallery-grid">
                    {allImages.map((src, i) => (
                        <img
                            key={i}
                            src={src}
                            alt={`Suite Jacuzzi B&B Alma Suite Anzio ${i + 1}`}
                            onClick={() => setLightboxIndex(i)}
                            loading="lazy"
                        />
                    ))}
                </div>
            </section>

            {/* Features */}
            <section className="suite-features">
                <motion.div
                    ref={featRef}
                    className="suite-features-inner"
                    initial={{ opacity: 0, y: 40 }}
                    animate={featInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <h2>{T.featTitle}</h2>
                    <p className="suite-features-sub">{T.featSub}</p>
                    <div className="suite-features-grid">
                        {feats.map((f, i) => (
                            <div className="suite-feature-item" key={i}>
                                <span className="suite-feature-icon">{f.icon}</span>
                                <h3>{f.title}</h3>
                                <p>{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* FAQ */}
            <section className="suite-faq">
                <motion.div
                    ref={faqRef}
                    className="suite-faq-inner"
                    initial={{ opacity: 0, y: 40 }}
                    animate={faqInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <h2>{T.faqTitle}</h2>
                    {faqs.map((faq, i) => (
                        <div className="faq-item" key={i}>
                            <button
                                className="faq-question"
                                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                            >
                                {faq.q}
                                <span className={`faq-icon${openFaq === i ? ' open' : ''}`}>+</span>
                            </button>
                            <div className={`faq-answer${openFaq === i ? ' open' : ''}`}>
                                <p>{faq.a}</p>
                            </div>
                        </div>
                    ))}
                </motion.div>
            </section>

            {/* CTA */}
            <section className="suite-cta-section">
                <h2>{T.ctaTitle}</h2>
                <p>{T.ctaSub}</p>
                <div className="suite-cta-buttons">
                    <a
                        href={`https://wa.me/+393928729907?text=${encodeURIComponent("Salve, vorrei prenotare la Suite Jacuzzi. www.almasuitebb.it")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                    >
                        {T.ctaBook}
                    </a>
                    <a href="mailto:almasuitebb@gmail.com" className="btn-outline-white">
                        {T.ctaEmail}
                    </a>
                </div>
            </section>

            {/* Lightbox */}
            {lightboxIndex !== null && (
                <Lightbox
                    images={allImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                />
            )}
        </div>
    );
}

export default SuiteJacuzziPage;
